import { Tooltip } from '@mantine/core'
import type { ReactElement, ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { formatNameList, getParticipantDisplayName } from '../presentation'
import type { PractitionerThreadOption } from '../service'
import { ParticipantAvatar } from './ParticipantAvatar'

type ParticipantAvatarGroupProps = {
  participants: PractitionerThreadOption[]
  size?: number
  /**
   * Draw the participants in a row rather than tiled into one avatar's square.
   * For the thread header, which has the width to spare and where `+2` was the
   * only trace of the third and fourth members.
   */
  expanded?: boolean
}

/** Tiles in the compact square: a 2×2 grid, the last becoming `+N` when it must. */
const GRID_TILES = 4

/** Avatars in the header row before the counter takes over, or it crowds the title. */
const EXPANDED_MAX_AVATARS = 5

/** Gap between compact tiles — enough to read as separate circles, no more. */
const TILE_GAP = 2

/** Matches the receipt tooltips, the other hover label in this view. */
const TOOLTIP_STYLES = { tooltip: { background: '#2B2F36', fontSize: 12, fontWeight: 500 } }

/**
 * Avatars for a conversation's other participants: a single avatar for a 1:1
 * thread, and for a group either a 2×2 tile grid (conversation rows) or an
 * overlapping row (the thread header).
 *
 * **The compact grid never leaves its square.** Avatars in a row used to be
 * drawn overlapping inside a box declared one avatar wide, so a third
 * participant pushed the stack ~29px past it and over the conversation title.
 * Tiling into the square instead keeps every row's text starting at the same x
 * — which is what the fixed width was protecting — without anything spilling.
 *
 * Decorative: every caller renders the participant names as text alongside it,
 * so this is hidden from assistive tech rather than repeating them. The hover
 * labels are a pointer shortcut to those same names, which is why they add no
 * focusable element inside the hidden subtree.
 */
export const ParticipantAvatarGroup = ({
  participants,
  size = 40,
  expanded = false,
}: ParticipantAvatarGroupProps) => {
  if (participants.length <= 1) {
    return <ParticipantAvatar practitioner={participants[0]?.practitioner} size={size} />
  }

  const limit = expanded ? EXPANDED_MAX_AVATARS : GRID_TILES
  // The counter costs a slot, so it only appears when it can name more than the
  // participant it would displace.
  const shown = participants.length <= limit ? participants : participants.slice(0, limit - 1)
  const hidden = participants.slice(shown.length)

  const withTooltip = (label: string, key: string, element: ReactElement) => (
    <Tooltip key={key} label={label} withArrow styles={TOOLTIP_STYLES}>
      {element}
    </Tooltip>
  )

  const slot = (key: string, label: string, className: string, child: ReactNode) =>
    withTooltip(label, key, <div className={cn(['relative shrink-0', className])}>{child}</div>)

  // Expanded: an overlapping row, sized by its contents. `hover:z-10` because
  // later avatars paint over earlier ones, so without it the left of the stack
  // can never be seen whole however hard you point at it.
  if (expanded) {
    const avatarSize = Math.round(size * 0.72)
    return (
      <div
        aria-hidden
        className="flex shrink-0 items-center"
        style={{ height: size, paddingTop: Math.round((size - avatarSize) / 2) }}
      >
        {shown.map((participant, index) =>
          slot(
            participant.practitioner.id!,
            getParticipantDisplayName(participant),
            cn([index > 0 && '-ml-2', 'hover:z-10']),
            <ParticipantAvatar
              practitioner={participant.practitioner}
              size={avatarSize}
              className="ring-2 ring-white"
            />,
          ),
        )}
        {hidden.length > 0 &&
          slot(
            'remaining',
            formatNameList(hidden.map(getParticipantDisplayName)),
            '-ml-2 hover:z-10',
            <RemainingTile count={hidden.length} size={avatarSize} ring />,
          )}
      </div>
    )
  }

  // Compact: tiles that wrap two per line, filling exactly the square a single
  // avatar would have occupied.
  const tileSize = Math.floor((size - TILE_GAP) / 2)
  return (
    <div
      aria-hidden
      className="flex shrink-0 flex-wrap content-center justify-center"
      style={{ width: size, height: size, gap: TILE_GAP }}
    >
      {shown.map((participant) =>
        slot(
          participant.practitioner.id!,
          getParticipantDisplayName(participant),
          '',
          <ParticipantAvatar practitioner={participant.practitioner} size={tileSize} />,
        ),
      )}
      {hidden.length > 0 &&
        slot(
          'remaining',
          formatNameList(hidden.map(getParticipantDisplayName)),
          '',
          <RemainingTile count={hidden.length} size={tileSize} />,
        )}
    </div>
  )
}

const RemainingTile = ({ count, size, ring }: { count: number; size: number; ring?: boolean }) => (
  <span
    className={cn([
      'inline-flex items-center justify-center rounded-full bg-[#E4E7EB] font-semibold text-[#6B7280]',
      ring && 'ring-2 ring-white',
    ])}
    style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.34)) }}
  >
    +{count}
  </span>
)
